import { useState, useCallback } from 'react';
import { Share2, MessageSquare, Bookmark, MapPin, Loader2, Download, X, Copy, Check } from 'lucide-react';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Canvas helpers
const roundRect = (ctx, x, y, w, h, r) => {
  ctx.beginPath();
  ctx.moveTo(x + r, y);
  ctx.lineTo(x + w - r, y);
  ctx.quadraticCurveTo(x + w, y, x + w, y + r);
  ctx.lineTo(x + w, y + h - r);
  ctx.quadraticCurveTo(x + w, y + h, x + w - r, y + h);
  ctx.lineTo(x + r, y + h);
  ctx.quadraticCurveTo(x, y + h, x, y + h - r);
  ctx.lineTo(x, y + r);
  ctx.quadraticCurveTo(x, y, x + r, y);
  ctx.closePath();
};

const wrapText = (ctx, text, x, y, maxW, lh) => {
  const words = text.split(' ');
  let line = '';
  const lines = [];
  for (const w of words) {
    const test = line + w + ' ';
    if (ctx.measureText(test).width > maxW && line) { lines.push(line); line = w + ' '; }
    else line = test;
  }
  lines.push(line);
  const startY = y - ((lines.length - 1) * lh) / 2;
  lines.forEach((l, i) => ctx.fillText(l.trim(), x, startY + i * lh));
};

export default function OfficialDarshanPost({ post, onNamaste, isMobile = false }) {
  const [showShareModal, setShowShareModal] = useState(false);
  const [generating, setGenerating] = useState(false);
  const [copied, setCopied] = useState(false);
  const [imgError, setImgError] = useState(false);

  const generateShareImage = useCallback(async () => {
    const canvas = document.createElement('canvas');
    const ctx = canvas.getContext('2d');
    canvas.width = 1080;
    canvas.height = 1920;

    return new Promise((resolve) => {
      const draw = () => {
        ctx.font = '120px serif';
        ctx.fillStyle = 'rgba(255,153,51,0.8)';
        ctx.textAlign = 'center';
        ctx.fillText('\u0950', 540, 250);
        // Day badge
        ctx.fillStyle = 'rgba(255,153,51,0.9)';
        roundRect(ctx, 40, 60, 300, 50, 25);
        ctx.fill();
        ctx.fillStyle = '#fff';
        ctx.font = 'bold 24px sans-serif';
        ctx.fillText(`${post.dayName}'s Darshan`, 190, 95);
        // Temple
        ctx.fillStyle = '#fff';
        ctx.font = 'bold 64px sans-serif';
        ctx.fillText(post.templeName, 540, 1270);
        ctx.font = '36px sans-serif';
        ctx.fillStyle = '#FF9933';
        ctx.fillText(post.deity, 540, 1340);
        ctx.font = '28px sans-serif';
        ctx.fillStyle = 'rgba(255,255,255,0.7)';
        ctx.fillText(post.location, 540, 1390);
        // Quote box
        ctx.fillStyle = 'rgba(0,0,0,0.5)';
        roundRect(ctx, 60, 1440, 960, 200, 20);
        ctx.fill();
        ctx.font = 'bold 48px serif';
        ctx.fillStyle = '#FF9933';
        ctx.textAlign = 'center';
        ctx.fillText(post.quote, 540, 1520);
        ctx.font = 'italic 28px sans-serif';
        ctx.fillStyle = '#fff';
        wrapText(ctx, `"${post.meaning}"`, 540, 1590, 920, 36);
        // Watermark
        ctx.fillStyle = 'rgba(255,255,255,0.6)';
        ctx.font = '24px sans-serif';
        ctx.fillText('Shared via DharmBook', 540, 1820);
        ctx.font = 'bold 32px serif';
        ctx.fillStyle = '#FF9933';
        ctx.fillText('DharmBook', 540, 1870);
        resolve(canvas.toDataURL('image/png'));
      };
      const img = new Image();
      img.crossOrigin = 'anonymous';
      img.onload = () => {
        const s = Math.max(1080 / img.width, 1920 / img.height);
        ctx.drawImage(img, (1080 - img.width * s) / 2, (1920 - img.height * s) / 2, img.width * s, img.height * s);
        const g = ctx.createLinearGradient(0, 0, 0, 1920);
        g.addColorStop(0, 'rgba(0,0,0,0.3)');
        g.addColorStop(0.5, 'rgba(0,0,0,0.1)');
        g.addColorStop(1, 'rgba(0,0,0,0.8)');
        ctx.fillStyle = g;
        ctx.fillRect(0, 0, 1080, 1920);
        draw();
      };
      img.onerror = () => {
        const g = ctx.createLinearGradient(0, 0, 0, 1920);
        g.addColorStop(0, '#1a1a2e');
        g.addColorStop(0.5, '#16213e');
        g.addColorStop(1, '#0f3460');
        ctx.fillStyle = g;
        ctx.fillRect(0, 0, 1080, 1920);
        draw();
      };
      img.src = post.imageUrl || post.media_url;
    });
  }, [post]);

  const handleShareBlessing = async () => {
    setGenerating(true);
    try {
      const imageDataUrl = await generateShareImage();
      const token = localStorage.getItem('dharmbook_token');
      fetch(`${API}/daily-darshan/share`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` }
      }).catch(() => {});

      if (navigator.share && navigator.canShare) {
        const blob = await (await fetch(imageDataUrl)).blob();
        const file = new File([blob], 'daily-darshan.png', { type: 'image/png' });
        if (navigator.canShare({ files: [file] })) {
          await navigator.share({
            title: `${post.dayName}'s Darshan - ${post.templeName}`,
            text: `\u{1F64F} ${post.quote}\n"${post.meaning}"\n\nGet your daily darshan on DharmBook!`,
            files: [file]
          });
          toast.success('Darshan shared!');
          return;
        }
      }
      setShowShareModal(true);
    } catch {
      setShowShareModal(true);
    } finally {
      setGenerating(false);
    }
  };

  const handleDownload = async () => {
    setGenerating(true);
    try {
      const dataUrl = await generateShareImage();
      const a = document.createElement('a');
      a.download = `darshan-${post.date}.png`;
      a.href = dataUrl;
      a.click();
      toast.success('Image downloaded!');
    } catch {
      toast.error('Download failed');
    } finally {
      setGenerating(false);
    }
  };

  const handleCopy = () => {
    navigator.clipboard.writeText(`${window.location.origin}/darshan`);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
    toast.success('Link copied!');
  };

  return (
    <>
      <article
        data-testid="official-darshan-post"
        className="bg-white rounded-2xl border border-orange-100/80 shadow-sm overflow-hidden"
        style={{ boxShadow: '0 0 0 1px rgba(251,146,60,0.1), 0 4px 16px rgba(251,146,60,0.06)' }}
      >
        {/* Header — looks like a user post */}
        <div className="flex items-center justify-between p-4 pb-2">
          <div className="flex items-center gap-3">
            <div className="w-11 h-11 rounded-full overflow-hidden shadow-sm flex-shrink-0 border-2 border-orange-200">
              <img src="/dharmbook-logo.png" alt="DharmBook" className="w-full h-full object-cover" />
            </div>
            <div>
              <div className="flex items-center gap-1.5">
                <span className="font-bold text-slate-800 text-sm">DharmBook</span>
                <svg className="w-4 h-4 text-orange-500 flex-shrink-0" viewBox="0 0 20 20" fill="currentColor">
                  <path fillRule="evenodd" d="M6.267 3.455a3.066 3.066 0 001.745-.723 3.066 3.066 0 013.976 0 3.066 3.066 0 001.745.723 3.066 3.066 0 012.812 2.812c.051.643.304 1.254.723 1.745a3.066 3.066 0 010 3.976 3.066 3.066 0 00-.723 1.745 3.066 3.066 0 01-2.812 2.812 3.066 3.066 0 00-1.745.723 3.066 3.066 0 01-3.976 0 3.066 3.066 0 00-1.745-.723 3.066 3.066 0 01-2.812-2.812 3.066 3.066 0 00-.723-1.745 3.066 3.066 0 010-3.976 3.066 3.066 0 00.723-1.745 3.066 3.066 0 012.812-2.812zm7.44 5.252a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                </svg>
              </div>
              <p className="text-[11px] text-slate-500 flex items-center gap-1.5">
                <span className="text-orange-600 font-semibold">Official</span>
                <span className="text-slate-300">&bull;</span>
                Today's Darshan
              </p>
            </div>
          </div>
          <span className="px-2 py-0.5 bg-orange-50 border border-orange-200/60 text-orange-600 text-[10px] font-bold rounded-full tracking-wide uppercase">
            Daily
          </span>
        </div>

        {/* Temple image with overlay */}
        <div className="relative mx-3 mt-1 rounded-xl overflow-hidden" style={{ aspectRatio: '16/10' }}>
          {!imgError ? (
            <img
              src={post.imageUrl || post.media_url}
              alt={post.templeName}
              className="absolute inset-0 w-full h-full object-cover"
              onError={() => setImgError(true)}
            />
          ) : (
            <div className="absolute inset-0 bg-gradient-to-br from-orange-800 via-amber-900 to-stone-900" />
          )}
          <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/20 to-transparent" />

          {/* Overlay content */}
          <div className="absolute inset-0 flex flex-col justify-end p-4">
            <p
              className="text-white font-bold leading-tight drop-shadow-lg"
              style={{
                fontFamily: 'Noto Sans Devanagari, serif',
                fontSize: isMobile ? '20px' : '24px',
                display: '-webkit-box',
                WebkitLineClamp: 2,
                WebkitBoxOrient: 'vertical',
                overflow: 'hidden',
              }}
            >
              {post.quote}
            </p>
            <p className="text-white/70 text-xs italic mt-1 line-clamp-1">"{post.meaning}"</p>
            <div className="flex items-center gap-2 mt-2">
              <span className="text-white/90 text-xs font-semibold">{post.templeName}</span>
              <span className="text-white/40">&bull;</span>
              <span className="text-white/60 text-[11px] flex items-center gap-0.5">
                <MapPin className="w-3 h-3" />
                {post.location}
              </span>
            </div>
          </div>
        </div>

        {/* Share Blessing CTA */}
        <div className="px-3 pt-3 pb-1">
          <button
            onClick={handleShareBlessing}
            disabled={generating}
            className="w-full flex items-center justify-center gap-2 py-2.5 bg-gradient-to-r from-orange-500 to-amber-500 hover:from-orange-600 hover:to-amber-600 text-white text-sm font-semibold rounded-xl transition-all active:scale-[0.98] disabled:opacity-60"
            data-testid="share-blessing-btn"
          >
            {generating ? (
              <Loader2 className="w-4 h-4 animate-spin" />
            ) : (
              <Share2 className="w-4 h-4" />
            )}
            Share Blessing
          </button>
        </div>

        {/* Footer — standard post actions */}
        <div className="flex items-center border-t border-stone-100 mt-2">
          <button
            onClick={() => onNamaste?.(post.id)}
            className={`flex-1 flex items-center justify-center gap-2 py-2.5 transition-colors ${
              post.is_liked ? 'text-orange-600 bg-orange-50' : 'text-slate-600 hover:bg-orange-50 hover:text-orange-600'
            }`}
            data-testid="darshan-namaste-btn"
          >
            <span className={isMobile ? 'text-lg' : 'text-base'}>&#x1F64F;</span>
            <span className="font-medium text-sm">Namaste</span>
          </button>
          <div className="w-px h-6 bg-stone-100" />
          <button className="flex-1 flex items-center justify-center gap-2 py-2.5 text-slate-600 hover:bg-stone-50">
            <MessageSquare className="w-4 h-4" />
            <span className="font-medium text-sm">Reflect</span>
          </button>
          <div className="w-px h-6 bg-stone-100" />
          <button
            onClick={handleShareBlessing}
            disabled={generating}
            className="flex-1 flex items-center justify-center gap-2 py-2.5 text-slate-600 hover:bg-stone-50 disabled:opacity-50"
          >
            {generating ? <Loader2 className="w-4 h-4 animate-spin" /> : <Share2 className="w-4 h-4" />}
            <span className="font-medium text-sm">Share</span>
          </button>
          <button className="px-4 py-2.5 text-slate-400 hover:text-orange-600">
            <Bookmark className="w-4 h-4" />
          </button>
        </div>
      </article>

      {/* Share Modal */}
      {showShareModal && (
        <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-end sm:items-center justify-center z-50 p-4" onClick={() => setShowShareModal(false)}>
          <div className="bg-white rounded-t-3xl sm:rounded-2xl w-full max-w-md p-6" onClick={e => e.stopPropagation()}>
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-bold text-slate-800">Share Darshan</h3>
              <button onClick={() => setShowShareModal(false)} className="p-2 hover:bg-slate-100 rounded-full"><X className="w-5 h-5 text-slate-500" /></button>
            </div>
            <p className="text-slate-600 text-sm mb-4">Share this divine blessing with your loved ones</p>
            <div className="space-y-3">
              <button onClick={handleDownload} className="w-full flex items-center gap-3 p-3 bg-orange-50 hover:bg-orange-100 rounded-xl transition-colors">
                <div className="w-10 h-10 bg-orange-500 rounded-full flex items-center justify-center"><Download className="w-5 h-5 text-white" /></div>
                <div className="text-left">
                  <p className="font-medium text-slate-800">Download Image</p>
                  <p className="text-xs text-slate-500">Save to share on WhatsApp, Instagram</p>
                </div>
              </button>
              <button onClick={handleCopy} className="w-full flex items-center gap-3 p-3 bg-slate-50 hover:bg-slate-100 rounded-xl transition-colors">
                <div className="w-10 h-10 bg-slate-600 rounded-full flex items-center justify-center">
                  {copied ? <Check className="w-5 h-5 text-white" /> : <Copy className="w-5 h-5 text-white" />}
                </div>
                <div className="text-left">
                  <p className="font-medium text-slate-800">{copied ? 'Copied!' : 'Copy Link'}</p>
                  <p className="text-xs text-slate-500">Share DharmBook with others</p>
                </div>
              </button>
              <a
                href={`https://wa.me/?text=${encodeURIComponent(`\u{1F64F} ${post.dayName}'s Darshan\n\n${post.templeName} - ${post.deity}\n\n${post.quote}\n"${post.meaning}"\n\nGet your daily darshan on DharmBook!\n${window.location.origin}`)}`}
                target="_blank" rel="noopener noreferrer"
                className="w-full flex items-center gap-3 p-3 bg-green-50 hover:bg-green-100 rounded-xl transition-colors"
              >
                <div className="w-10 h-10 bg-green-500 rounded-full flex items-center justify-center">
                  <svg className="w-5 h-5 text-white" fill="currentColor" viewBox="0 0 24 24"><path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/></svg>
                </div>
                <div className="text-left">
                  <p className="font-medium text-slate-800">Share on WhatsApp</p>
                  <p className="text-xs text-slate-500">Send blessing to contacts</p>
                </div>
              </a>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
